import { ShipmentInterface } from "../interfaces";

/**
 * Resolves the guías a delivery is about, taking whatever the caller already
 * holds and reading only what is missing.
 *
 * The caller used to be trusted all-or-nothing: either it passed every guía or
 * it passed none and the modal read all of them one by one. That made the
 * detail screen re-read guías it had in memory, and it made a partial list
 * from the caller silently deliver fewer guías than the cashier ticked.
 *
 * Returns null when any number cannot be resolved, so the caller can abort
 * before creating a single POD. A guía skipped in silence is worse than a
 * failed delivery: it leaves the cashier believing a package was signed for.
 *
 * Reads stay serial on purpose. The job here is to issue fewer requests, not
 * the same ones faster, and the shared axios instance renews the session on
 * each 401 with no mutex, so a burst that outlives a token fires several
 * renewals at once.
 */
export const resolveShipmentsToDeliver = async (
  shipmentNumbers: string[],
  provided: ShipmentInterface[] | undefined,
  fetchShipment: (shipmentNumber: string) => Promise<ShipmentInterface | null>
): Promise<ShipmentInterface[] | null> => {
  const byNumber = new Map<string, ShipmentInterface>();
  for (const shipment of provided ?? []) {
    if (shipment?.shipmentNumber) {
      byNumber.set(shipment.shipmentNumber, shipment);
    }
  }

  // A caller may derive the numbers from the very guías it hands over: pick-up
  // does, with shipments.map((s) => s?.shipmentNumber ?? ""). An empty entry
  // there means "the guía in this position carries no number", not "go and
  // look it up", so take it from the list the caller gave when the two line up.
  const alignedProvided =
    provided && provided.length === shipmentNumbers.length ? provided : undefined;

  // An empty number that nothing covers would reach the API as
  // ?ShipmentNumber= and answer 500: the server parses it as a number and then
  // as a GUID, and neither is guarded. Refuse before spending any request,
  // since the delivery is going to be aborted anyway.
  const unresolvable = shipmentNumbers.some(
    (shipmentNumber, index) => !shipmentNumber && !alignedProvided?.[index]
  );
  if (unresolvable) return null;

  for (const shipmentNumber of shipmentNumbers) {
    // A number already held costs nothing, and a repeated number is read once.
    if (!shipmentNumber || byNumber.has(shipmentNumber)) continue;
    const shipment = await fetchShipment(shipmentNumber);
    if (shipment) byNumber.set(shipmentNumber, shipment);
  }

  const resolved = shipmentNumbers.map((shipmentNumber, index) =>
    shipmentNumber ? byNumber.get(shipmentNumber) : alignedProvided?.[index]
  );
  if (resolved.some((shipment) => !shipment)) return null;

  return resolved as ShipmentInterface[];
};
